import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../api/axios";
import { FaPlusCircle, FaMoneyCheckAlt } from "react-icons/fa";

const PaymentList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const res = await api.get("payment_vouchers/");
      let data = Array.isArray(res.data) ? res.data : (res.data?.payment_vouchers || []);
      
      // Filter by type if in URL
      const searchParams = new URLSearchParams(window.location.search);
      const typeFilter = searchParams.get("type");
      if (typeFilter) {
        data = data.filter(r => r.party_type === typeFilter);
      }

      setPayments(data);
    } catch (err) {
      console.error("Error fetching payments:", err);
      setError("Failed to load payment vouchers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [location.search]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "AED",
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) return (
    <div className="container mt-4 text-center">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
      <p className="mt-2">Loading payments...</p>
    </div>
  );

  return (
    <div className="container-fluid mt-4 px-4">
      <div className="card shadow-sm border-0">
        <div className="card-body p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h3 className="mb-0 text-[#0077cc] fw-bold d-flex align-items-center gap-2">
              <FaMoneyCheckAlt /> Payment Vouchers List
            </h3>
            <div>
              <button 
                className="btn btn-primary d-flex align-items-center gap-2" 
                onClick={() => navigate("/dashboard/payments/new")}
              >
                <FaPlusCircle /> Add Payment
              </button>
            </div>
          </div>

          {error && <div className="alert alert-danger">{error}</div>}

          <div className="table-responsive">
            <table className="table table-hover align-middle" style={{fontSize: "0.9rem"}}>
              <thead className="table-light">
                <tr>
                  <th>ID</th>
                  <th>Date</th>
                  <th>Party (Customer/Supplier)</th>
                  <th>Sale ID</th>
                  <th>Method</th>
                  <th>Bank / Cheque</th>
                  <th>Amount</th>
                  <th>Tax</th>
                  <th>Total</th>
                  <th>Recon. Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center py-4 text-muted">
                      No payment vouchers found.
                    </td>
                  </tr>
                ) : (
                  payments.map((payment) => (
                    <tr key={payment.id}>
                      <td><strong>#{payment.voucher_number || payment.id}</strong></td>
                      <td>{formatDate(payment.payment_date)}</td>
                      <td>
                        <span className={`badge ${payment.party_type === 'Customer' ? 'bg-primary' : 'bg-warning text-dark'} me-2`}>
                          {payment.party_type}
                        </span>
                        {payment.customer_name || payment.supplier_name || "—"}
                      </td>
                      <td>{payment.sales_id ? `#${payment.sales_id}` : "—"}</td>
                      <td>{payment.payment_method || "—"}</td>
                      <td>
                        {payment.bank_name && <div className="small">Bank: {payment.bank_name}</div>}
                        {payment.cheque_number && <div className="small text-muted">Chq: {payment.cheque_number}</div>}
                      </td>
                      <td className="fw-bold">{formatCurrency(payment.amount)}</td>
                      <td className="text-muted">{formatCurrency(payment.tax_amount)}</td>
                      <td className="fw-bold text-danger">
                        {formatCurrency(Number(payment.amount) + Number(payment.tax_amount))}
                      </td>
                      <td>
                        {["Bank Transfer", "Cheque"].includes(payment.payment_method) ? (
                          payment.is_reconciled ? (
                            <span className="badge" style={{background:"#d4edda",color:"#155724",fontSize:"0.78rem",padding:"4px 8px",borderRadius:"12px"}}>
                              ✓ Reconciled
                            </span>
                          ) : (
                            <span className="badge" style={{background:"#fff3cd",color:"#856404",fontSize:"0.78rem",padding:"4px 8px",borderRadius:"12px"}}>
                              ⏳ Unreconciled
                            </span>
                          )
                        ) : (
                          <span className="text-muted" style={{fontSize:"0.78rem"}}>N/A</span>
                        )}
                      </td>
                      <td>
                        <button 
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => navigate(`/dashboard/payments/view/${payment.id}`)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentList;
